import { Button, Form, IFormRender } from '@busybusy/webapp-react-ui';
import classNames from 'classnames';
import { ClassName } from "types/ClassName";
import Typography from 'components/foundation/text/Typography/Typography';
import HeaderText from 'containers/settings/components/HeaderText/HeaderText';
import { useToastOpen } from 'contexts/ToastContext';
import { usePositionUpdate } from 'hooks';
import { t } from 'i18next';
import { isEqual } from 'lodash';
import { useState } from 'react';
import { PositionManageBudgetType } from 'utils/positionUtils';
import BudgetFormFields, { IBudgetFieldsData } from '../components/BudgetFormFields/BudgetFormFields';

export interface IBudgetFormProps {
  className?: ClassName;
  positionId: string;
  onSubmit: (formData: { manageBudgets: boolean; viewBudgets: boolean }) => void;
  currentSetting: PositionManageBudgetType;
}

const BudgetForm = (props: IBudgetFormProps) => {
  const { positionId, onSubmit, currentSetting, className } = props;

  const [, update] = usePositionUpdate();
  const openToast = useToastOpen();
  const [formInfo, setFormInfo] = useState<IBudgetFieldsData>({ manageBudgets: currentSetting });

  async function onFormSubmit(formData: IBudgetFieldsData) {
    let updatedValue = {
      manageBudgets: false,
      viewBudgets: false,
    };

    switch (formData.manageBudgets) {
      case PositionManageBudgetType.MANAGE:
        updatedValue = {
          manageBudgets: true,
          viewBudgets: true,
        };
        break;
      case PositionManageBudgetType.VIEW_ONLY:
        updatedValue = {
          manageBudgets: false,
          viewBudgets: true,
        };
        break;
      case PositionManageBudgetType.OFF:
        updatedValue = {
          manageBudgets: false,
          viewBudgets: false,
        };
        break;
    }

    if (!isEqual(currentSetting, updatedValue)) {
      try {
        await update({ ...updatedValue, id: positionId });
      } catch (e) {
        openToast({ label: t('Something went wrong. Please try again later.') });
      }
    }

    onSubmit(updatedValue);
  }

  function renderFormFields(form: IFormRender<IBudgetFieldsData>) {
    return (
      <>
        <BudgetFormFields form={form} />
        <Button type="primary" onClick={form.handleSubmit}>
          {t('Update')}
        </Button>
      </>
    );
  }

  function handleChange(formData: IBudgetFieldsData | undefined) {
    if (formData) {
      setFormInfo(formData);
    }
  }

  const classes = classNames('budget-form', className);

  return (
    <div className={classes}>
      <HeaderText title={t('Budgets')} />
      <Typography tag="span" className="mb-4">
        {formInfo.manageBudgets === PositionManageBudgetType.VIEW_ONLY
          ? t(
              'The employee will be able to view budget information, but won’t be able to create or edit project budgets.'
            )
          : t(
              'View, create, edit and delete project budgets. Optionally allow the user to view, but not modify, budgets.'
            )}
      </Typography>
      <Form
        className="mt-4"
        render={renderFormFields}
        onSubmit={onFormSubmit}
        data={formInfo}
        onChange={handleChange}
      />
    </div>
  );
};

export default BudgetForm;
